/**
 * PATH: packages/ui/src/components/settings/TabbedSettingsPanel.tsx
 * PURPOSE: Horizontal sub-tabs within a single settings sidebar entry.
 */

import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface TabbedPanel<T extends string = string> {
  id: T
  label: string
  icon?: LucideIcon
  content: ReactNode
}

export interface TabbedSettingsPanelProps<T extends string> {
  panels: TabbedPanel<T>[]
  activePanel: T
  onPanelChange: (id: T) => void
  className?: string
}

export function TabbedSettingsPanel<T extends string>({
  panels,
  activePanel,
  onPanelChange,
  className,
}: TabbedSettingsPanelProps<T>) {
  const active = panels.find((p) => p.id === activePanel) ?? panels[0]

  return (
    <div className={cn('space-y-6', className)}>
      <div
        role="tablist"
        className="inline-flex flex-wrap gap-1 rounded-lg border border-slate-200 dark:border-slate-700 p-0.5"
      >
        {panels.map((panel) => {
          const Icon = panel.icon
          const isActive = panel.id === activePanel
          return (
            <button
              key={panel.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onPanelChange(panel.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800',
              )}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {panel.label}
            </button>
          )
        })}
      </div>
      <div role="tabpanel">{active?.content}</div>
    </div>
  )
}
